import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['typingInput', 'cursor']

  connect() {
    this.textToType = 'Italian restaurants near me'
    this.typingSpeed = 60 // ms per char
    this.fadeDuration = 600 // matches the CSS opacity transition on .search-mockup
    this.firstRun = true

    // Start typing animation only when the card is visible on screen
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Stop observing so the observer only fires once
            this.observer.unobserve(entry.target)
            
            // Brief wait so the bento card's scroll-reveal slide-up is underway before typing begins
            this.startDelayTimeout = setTimeout(() => {
              this.startAnimation()
            }, 300)
          }
        })
      }, { threshold: 0.15 })
      
      this.observer.observe(this.element)
    } else {
      // Fallback if IntersectionObserver is not supported
      this.startAnimation()
    }
  }

  disconnect() {
    this.stopAnimation()
    if (this.observer) {
      this.observer.disconnect()
    }
    clearTimeout(this.startDelayTimeout)
  }

  startAnimation() {
    this.resetState() // 'search-mockup state-initial' (no .is-visible) -> invisible, blank bar

    // Commit the opacity:0 baseline, then fade the blank search bar in
    void this.element.offsetWidth
    this.element.classList.add('is-visible')

    // Hold the faded-in bar for a beat (longer on first run, snappier on repeats),
    // then expand the results and start typing
    const holdAfterFadeIn = this.firstRun ? 1000 : 500
    this.firstRun = false

    this.typeTimeout = setTimeout(() => {
      this.element.classList.remove('state-initial')
      this.element.classList.add('state-typing')
      this.typeCharacter(0)
    }, this.fadeDuration + holdAfterFadeIn)
  }

  stopAnimation() {
    clearTimeout(this.typeTimeout)
    clearTimeout(this.typeInterval)
    clearTimeout(this.sortingTimeout)
    clearTimeout(this.placedTimeout)
    clearTimeout(this.focusedTimeout)
    clearTimeout(this.resetTimeout)
  }

  resetState() {
    this.stopAnimation()
    this.element.className = 'search-mockup state-initial'
    this.typingInputTarget.textContent = '\u200B'
    this.cursorTarget.style.opacity = '1'
  }

  typeCharacter(index) {
    if (index < this.textToType.length) {
      // Clear zero-width space before typing first character if present
      if (this.typingInputTarget.textContent === '\u200B') {
        this.typingInputTarget.textContent = ''
      }
      this.typingInputTarget.textContent += this.textToType.charAt(index)
      this.typeInterval = setTimeout(() => this.typeCharacter(index + 1), this.typingSpeed)
    } else {
      // Finished typing -> hide cursor, pause for 1000ms, then transition to sorting state
      this.cursorTarget.style.opacity = '0'
      this.sortingTimeout = setTimeout(() => this.showSorting(), 1000)
    }
  }

  showSorting() {
    this.element.classList.remove('state-typing')
    this.element.classList.add('state-sorting')
    
    // Duomo 51 lifts and moves up to the top, takes 1.8 seconds to translate
    this.placedTimeout = setTimeout(() => this.showPlaced(), 1800)
  }

  showPlaced() {
    this.element.classList.remove('state-sorting')
    this.element.classList.add('state-placed')
    
    // Duomo 51 settles back down flat, stays placed for 1.2 seconds before collapse
    this.focusedTimeout = setTimeout(() => this.showFocused(), 1200)
  }

  showFocused() {
    this.element.classList.remove('state-placed')
    this.element.classList.add('state-focused')
    
    // Collapses down to only the Duomo 51 card. Visible for 4.0 seconds, then transition to resetting
    this.resetTimeout = setTimeout(() => this.showResetting(), 4000)
  }

  showResetting() {
    // Fade the whole card away in place (stay in state-focused so the Duomo card
    // fades rather than snapping back to the collapsed layout)
    this.element.classList.remove('is-visible')

    // Clear search text so the next cycle's bar fades in blank
    this.typingInputTarget.textContent = '\u200B'

    // Once the fade-out finishes, restart: reset to the blank bar, fade it back in,
    // hold, then type again
    this.typeTimeout = setTimeout(() => this.startAnimation(), this.fadeDuration)
  }
}
